<template>
  <v-container fluid class="mt-16 px-6">
    <v-card>
      <v-card-title class="d-flex flex-wrap pa-5" style="gap: 8px">
        <h2>RMA Items</h2>
        <v-spacer></v-spacer>
        <v-btn small color="success" @click="print_all()">Print All</v-btn>
        <v-btn small color="success" @click="print_multi()"
          >Print Multiple</v-btn
        >
        <v-btn small v-if="print_multiple" @click="print_selected_multiple()"
          >Print Selected - {{ checkbox_array.length }}</v-btn
        >
        <add-return-item-modal />
      </v-card-title>

      <v-card-text>
        <!-- Plain-language flow guide -->
        <v-alert dense text type="info" class="mb-3 caption">
          Each RMA has two independent sides. <strong>Customer</strong>: give
          the customer their replacement (same or a different item).
          <strong>Supplier</strong>: send the defective unit back and record the
          supplier's result. You can do either side first — the RMA is
          <strong>Resolved</strong> once both are done.
        </v-alert>

        <!-- Filter + search -->
        <div class="d-flex flex-wrap align-center mb-2" style="gap: 12px">
          <v-select
            dense
            hide-details
            outlined
            style="max-width: 280px"
            :items="statusFilters"
            v-model="statusFilter"
            label="Filter"
          ></v-select>
          <v-text-field
            dense
            hide-details
            outlined
            style="max-width: 320px"
            label="Search"
            v-model="search"
            prepend-inner-icon="mdi-magnify"
          ></v-text-field>
        </div>

        <v-data-table
          dense
          :search="search"
          :headers="tableHeaders"
          :items="filteredItems"
          :items-per-page="15"
        >
          <!-- Lot / Supplier -->
          <template v-slot:item.stock_lot="{ item }">
            <span v-if="item.stock_lot">
              {{ item.stock_lot.supplier || "N/A" }}
              <span class="grey--text"
                >(₱{{
                  Number(item.stock_lot.unit_cost).toLocaleString()
                }})</span
              >
            </span>
            <span v-else class="grey--text">No batch</span>
          </template>

          <template v-slot:item.lot_warranty="{ item }">
            <warranty-chip :lot="item.stock_lot" />
          </template>

          <!-- CUSTOMER side -->
          <template v-slot:item.customer="{ item }">
            <v-chip
              x-small
              :color="cs(item) === 'replaced' ? 'blue' : 'orange'"
              dark
            >
              <v-icon x-small class="mr-1">{{
                cs(item) === "replaced"
                  ? "mdi-account-check"
                  : "mdi-account-clock"
              }}</v-icon>
              {{ cs(item) === "replaced" ? "Replaced" : "Not replaced" }}
            </v-chip>
            <div
              v-if="
                cs(item) === 'replaced' &&
                item.replacement_item &&
                item.replacement_item_id !== item.item_id
              "
              class="caption grey--text mt-1"
            >
              → {{ item.replacement_item.item_name
              }}<span v-if="item.replacement_quantity">
                ×{{ item.replacement_quantity }}</span
              >
            </div>
            <v-btn
              v-if="cs(item) === 'not_replaced'"
              x-small
              color="success"
              class="mt-1"
              @click="openGiveReplacement(item)"
              :loading="actionLoading === item.id"
            >
              <v-icon x-small class="mr-1">mdi-swap-horizontal</v-icon>
              Give Replacement
            </v-btn>
          </template>

          <!-- SUPPLIER side -->
          <template v-slot:item.supplier_track="{ item }">
            <v-chip x-small :color="supplierMeta(item).color" dark>
              <v-icon x-small class="mr-1">{{
                supplierMeta(item).icon
              }}</v-icon>
              {{ supplierMeta(item).label }}
            </v-chip>
            <div
              class="mt-1 d-flex"
              style="gap: 4px; flex-wrap: wrap"
              v-if="['none', 'awaiting', 'returned'].includes(ss(item))"
            >
              <v-btn
                v-if="ss(item) === 'none'"
                x-small
                color="purple"
                dark
                @click="confirmMarkForSupplierReturn(item)"
                :loading="actionLoading === item.id"
              >
                <v-icon x-small class="mr-1">mdi-package-variant-closed</v-icon
                >Store to RMA Box
              </v-btn>
              <v-btn
                v-if="ss(item) === 'awaiting'"
                x-small
                color="teal"
                dark
                @click="openReturnToSupplierDialog(item)"
                :loading="actionLoading === item.id"
              >
                <v-icon x-small class="mr-1">mdi-truck-check</v-icon>Confirm
                Shipped
              </v-btn>
              <template v-if="ss(item) === 'returned'">
                <v-btn
                  x-small
                  color="green"
                  dark
                  @click="openSupplierReplacedDialog(item)"
                  :loading="actionLoading === item.id"
                >
                  <v-icon x-small class="mr-1"
                    >mdi-package-variant-closed</v-icon
                  >Replaced
                </v-btn>
                <v-btn
                  x-small
                  color="blue"
                  dark
                  @click="openSupplierCreditDialog(item)"
                  :loading="actionLoading === item.id"
                >
                  <v-icon x-small class="mr-1">mdi-cash-refund</v-icon>Credited
                </v-btn>
              </template>
              <v-btn
                x-small
                color="grey"
                dark
                @click="confirmWriteOff(item)"
                :loading="actionLoading === item.id"
              >
                <v-icon x-small class="mr-1">mdi-close-circle</v-icon>Write off
              </v-btn>
            </div>
          </template>

          <!-- Last updated (date + account) -->
          <template v-slot:item.updated="{ item }">
            <div v-if="item.updated_at">
              <div class="caption">{{ formatDate(item.updated_at) }}</div>
              <div class="caption grey--text">
                {{ item.updated_by || "—" }}
              </div>
            </div>
            <span v-else class="grey--text caption">—</span>
          </template>

          <!-- Utility actions (edit / print / delete) -->
          <template v-slot:item.utility="{ item }">
            <div class="d-flex align-center">
              <edit-return-item-modal
                v-if="!isFullyResolved(item)"
                :item="item"
              />
              <v-btn icon small color="green" @click="print(item)"
                ><v-icon small>mdi-printer</v-icon></v-btn
              >
              <v-btn
                icon
                small
                color="red"
                v-if="canDelete(item)"
                @click="del(item)"
                :loading="loading"
                ><v-icon small>mdi-delete</v-icon></v-btn
              >
            </div>
          </template>

          <template v-slot:item.check_box="{ item }" v-if="print_multiple">
            <v-checkbox
              @click="add_checkbox(item)"
              dense
              hide-details
            ></v-checkbox>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Give Replacement Dialog (same or different item) -->
    <v-dialog v-model="giveDialog" width="560" persistent>
      <v-card>
        <v-card-title>Give Replacement to Customer</v-card-title>
        <v-card-text>
          <p class="mb-2">
            Defective item:
            <strong>{{
              selectedItem ? selectedItem.item.item_name : ""
            }}</strong>
            (Qty {{ selectedItem ? selectedItem.quantity : "" }})
          </p>
          <v-radio-group v-model="giveMode" row hide-details class="mb-3">
            <v-radio label="Same item" value="same"></v-radio>
            <v-radio label="Different item" value="different"></v-radio>
          </v-radio-group>
          <v-autocomplete
            v-if="giveMode === 'different'"
            v-model="giveReplacementItemId"
            :items="itemPickerList"
            item-text="label"
            item-value="id"
            label="Replacement item"
            clearable
            dense
            outlined
          ></v-autocomplete>
          <v-text-field
            v-model.number="giveQty"
            type="number"
            label="Quantity to give"
            dense
            outlined
            style="max-width: 220px"
          ></v-text-field>
          <v-alert dense text type="info" class="caption mb-0">
            Deducts the chosen item from shelf stock (FIFO) and posts the
            journal entry. The defective unit's supplier claim is tracked
            separately on the Supplier side.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-btn
            color="success"
            dark
            @click="submitGiveReplacement"
            :loading="actionLoading === (selectedItem && selectedItem.id)"
            >Confirm</v-btn
          >
          <v-btn @click="giveDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirm Shipped Dialog -->
    <v-dialog v-model="returnToSupplierDialog" width="400" persistent>
      <v-card>
        <v-card-title>Confirm Shipped to Supplier</v-card-title>
        <v-card-text>
          <p>
            Item:
            <strong>{{
              selectedItem ? selectedItem.item.item_name : ""
            }}</strong>
          </p>
          <p v-if="selectedItem && selectedItem.stock_lot">
            Supplier: <strong>{{ selectedItem.stock_lot.supplier }}</strong>
          </p>
          <v-text-field
            v-model="returnDate"
            label="Date Returned to Supplier"
            type="date"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-btn
            color="teal"
            dark
            @click="submitReturnToSupplier"
            :loading="actionLoading === (selectedItem && selectedItem.id)"
            >Confirm</v-btn
          >
          <v-btn @click="returnToSupplierDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Supplier Credit Dialog -->
    <v-dialog v-model="supplierCreditDialog" width="400" persistent>
      <v-card>
        <v-card-title>Supplier Credit</v-card-title>
        <v-card-text>
          <p>
            Item:
            <strong>{{
              selectedItem ? selectedItem.item.item_name : ""
            }}</strong>
          </p>
          <p v-if="selectedItem && selectedItem.stock_lot">
            Supplier: <strong>{{ selectedItem.stock_lot.supplier }}</strong>
          </p>
          <v-text-field
            v-model="creditAmount"
            label="Credit Amount (₱)"
            type="number"
            prefix="₱"
          ></v-text-field>
          <p class="caption grey--text mb-0">
            Posts: Debit Supplier Credits for RMA / Credit Items for Replace.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-btn
            color="blue"
            dark
            @click="submitSupplierCredited"
            :loading="actionLoading === (selectedItem && selectedItem.id)"
            >Confirm</v-btn
          >
          <v-btn @click="supplierCreditDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Supplier Replaced Dialog (choose which active item gets the new lot) -->
    <v-dialog v-model="supplierReplacedDialog" width="560" persistent>
      <v-card>
        <v-card-title>Supplier Replaced</v-card-title>
        <v-card-text>
          <p class="mb-2">
            Defective item:
            <strong>{{
              selectedItem ? selectedItem.item.item_name : ""
            }}</strong>
            (Qty {{ selectedItem ? selectedItem.quantity : "" }})
          </p>
          <p class="mb-2 caption grey--text">
            Choose which item record receives the replacement stock. Items with
            the same name/type are listed separately so you can insert the new
            batch into the correct one. Deleted items are excluded.
          </p>
          <v-autocomplete
            v-model="supplierReplacedTargetItemId"
            :items="activeItemPickerList"
            item-text="label"
            item-value="id"
            label="Add replacement stock to"
            dense
            outlined
          ></v-autocomplete>
          <v-alert dense text type="info" class="caption mb-0">
            Adds a new stock batch to the chosen item and posts the journal
            entry (Dr Inventory / Cr Items for Replace).
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-btn
            color="green"
            dark
            @click="submitSupplierReplaced"
            :loading="actionLoading === (selectedItem && selectedItem.id)"
            >Confirm</v-btn
          >
          <v-btn @click="supplierReplacedDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";
import addReturnItemModal from "@/components/Item/addReturnItemModal.vue";
import EditReturnItemModal from "@/components/Item/editReturnItemModal.vue";
import WarrantyChip from "@/components/Item/WarrantyChip.vue";
import printMixin from "@/mixins/printMixin";

export default {
  name: "RmaItems",
  components: { addReturnItemModal, EditReturnItemModal, WarrantyChip },
  mixins: [printMixin],
  data() {
    return {
      statusFilter: "needs_action",
      search: "",
      print_multiple: false,
      checkbox_array: [],
      loading: false,
      actionLoading: null,
      // Give replacement dialog
      giveDialog: false,
      giveMode: "same",
      giveReplacementItemId: null,
      giveQty: 1,
      // Confirm shipped dialog
      returnToSupplierDialog: false,
      returnDate: new Date().toISOString().substring(0, 10),
      // Supplier credit dialog
      supplierCreditDialog: false,
      creditAmount: 0,
      // Supplier replaced dialog (choose target item for the new lot)
      supplierReplacedDialog: false,
      supplierReplacedTargetItemId: null,
      selectedItem: null,
    };
  },
  computed: {
    ...mapGetters({
      returned_items: "item/returned_items",
      items: "item/items",
      default_server_url: "auth/default_server_url",
      user: "auth/user",
    }),
    tableHeaders() {
      const base = [];
      if (this.print_multiple)
        base.push({ text: "", value: "check_box", sortable: false });
      base.push(
        { text: "ID", value: "id" },
        { text: "Item", value: "item.item_name" },
        { text: "Qty", value: "quantity" },
        { text: "S/N", value: "product_serial_number" },
        { text: "Batch / Supplier", value: "stock_lot", sortable: false },
        { text: "Warranty", value: "lot_warranty", sortable: false },
        { text: "Customer Status", value: "customer", sortable: false },
        { text: "Supplier Status", value: "supplier_track", sortable: false },
        { text: "Updated", value: "updated", sortable: false },
        { text: "", value: "utility", sortable: false }
      );
      return base;
    },
    itemPickerList() {
      return (this.items || []).map((i) => ({
        id: i.id,
        label: `${i.item_name} (stock: ${i.quantity})`,
      }));
    },
    // Active (non-deleted) items, labelled with enough detail to tell apart
    // records that share the same name/type when choosing where the supplier
    // replacement lot should be added.
    activeItemPickerList() {
      return (this.items || [])
        .filter((i) => !i.is_deleted)
        .map((i) => {
          const type = i.item_type ? i.item_type.item_type : "N/A";
          const supplier =
            (i.latest_lot && i.latest_lot.supplier) || i.supplier;
          return {
            id: i.id,
            label:
              `#${i.id} — ${i.item_name} · ${type}` +
              (supplier ? ` · ${supplier}` : "") +
              ` (stock: ${i.quantity})`,
          };
        });
    },
    statusFilters() {
      const count = (fn) => this.returned_items.filter(fn).length;
      return [
        { text: `All (${this.returned_items.length})`, value: "all" },
        {
          text: `Needs action (${count((p) => !this.isFullyResolved(p))})`,
          value: "needs_action",
        },
        {
          text: `Customer: not replaced (${count(
            (p) => this.cs(p) === "not_replaced"
          )})`,
          value: "customer_open",
        },
        {
          text: `Supplier: open (${count((p) =>
            ["none", "awaiting", "returned"].includes(this.ss(p))
          )})`,
          value: "supplier_open",
        },
        {
          text: `Resolved (${count((p) => this.isFullyResolved(p))})`,
          value: "resolved",
        },
      ];
    },
    filteredItems() {
      const f = this.statusFilter;
      return this.returned_items.filter((p) => {
        if (f === "all") return true;
        if (f === "needs_action") return !this.isFullyResolved(p);
        if (f === "resolved") return this.isFullyResolved(p);
        if (f === "customer_open") return this.cs(p) === "not_replaced";
        if (f === "supplier_open")
          return ["none", "awaiting", "returned"].includes(this.ss(p));
        return true;
      });
    },
  },
  methods: {
    formatDate(date) {
      return date ? moment(date).format("MMM D, YYYY h:mm A") : "—";
    },
    // === Status helpers ===
    cs(item) {
      return (
        item.customer_status ||
        (item.date_replaced ? "replaced" : "not_replaced")
      );
    },
    ss(item) {
      return item.supplier_status || "none";
    },
    isFullyResolved(item) {
      return (
        this.cs(item) === "replaced" &&
        ["resolved_replaced", "resolved_credited", "written_off"].includes(
          this.ss(item)
        )
      );
    },
    canDelete(item) {
      return ![
        "resolved_replaced",
        "resolved_credited",
        "written_off",
      ].includes(this.ss(item));
    },
    supplierMeta(item) {
      const map = {
        none: { label: "Not sent", color: "grey", icon: "mdi-store" },
        awaiting: {
          label: "Awaiting",
          color: "purple",
          icon: "mdi-truck-delivery",
        },
        returned: {
          label: "At supplier",
          color: "teal",
          icon: "mdi-truck-check",
        },
        resolved_replaced: {
          label: "Replaced",
          color: "green",
          icon: "mdi-check-circle",
        },
        resolved_credited: {
          label: "Credited",
          color: "green",
          icon: "mdi-cash-refund",
        },
        written_off: {
          label: "Written off",
          color: "red",
          icon: "mdi-close-circle",
        },
      };
      return map[this.ss(item)] || map.none;
    },

    // === Customer side ===
    openGiveReplacement(item) {
      this.selectedItem = item;
      this.giveMode = "same";
      this.giveReplacementItemId = null;
      this.giveQty = item.quantity || 1;
      this.giveDialog = true;
    },
    async submitGiveReplacement() {
      const sameItem = this.giveMode === "same";
      const replacementItemId = sameItem
        ? this.selectedItem.item_id
        : this.giveReplacementItemId;
      if (!sameItem && !replacementItemId) {
        this.$alert({
          title: "Validation",
          message: "Please choose a replacement item.",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }
      const qty = this.giveQty > 0 ? this.giveQty : this.selectedItem.quantity;
      this.actionLoading = this.selectedItem.id;
      try {
        await this.give_replacement({
          id: this.selectedItem.id,
          replacement_item_id: replacementItemId,
          replacement_quantity: qty,
        });
        this.$alert({
          title: "Success",
          message: "Replacement given. Stock deducted from shelf.",
          color: "success",
          icon: "mdi-check-circle",
        });
        this.giveDialog = false;
      } catch (err) {
        this.$alert({
          title: "Error",
          message:
            "Error: " + (err.response ? err.response.data.error : err.message),
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
      this.actionLoading = null;
    },

    // === Supplier side ===
    async confirmMarkForSupplierReturn(item) {
      let x = await this.$confirm({
        title: "Send to Supplier",
        message:
          "Flag this defective item to return to the supplier? (No stock or accounting change yet.)",
        color: "primary",
        icon: "mdi-help-circle-outline",
      });
      if (!x) return;
      this.actionLoading = item.id;
      try {
        await this.mark_supplier_return(item.id);
        this.$alert({
          title: "Success",
          message: "Marked for supplier RMA.",
          color: "success",
          icon: "mdi-check-circle",
        });
      } catch (err) {
        this.$alert({
          title: "Error",
          message:
            "Error: " + (err.response ? err.response.data.error : err.message),
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
      this.actionLoading = null;
    },

    openReturnToSupplierDialog(item) {
      this.selectedItem = item;
      this.returnDate = new Date().toISOString().substring(0, 10);
      this.returnToSupplierDialog = true;
    },
    async submitReturnToSupplier() {
      if (!this.returnDate) {
        this.$alert({
          title: "Validation",
          message: "Please select a date.",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }
      this.actionLoading = this.selectedItem.id;
      try {
        await this.return_to_supplier({
          id: this.selectedItem.id,
          date_returned: this.returnDate,
        });
        this.$alert({
          title: "Success",
          message: "Item returned to supplier.",
          color: "success",
          icon: "mdi-check-circle",
        });
        this.returnToSupplierDialog = false;
      } catch (err) {
        this.$alert({
          title: "Error",
          message:
            "Error: " + (err.response ? err.response.data.error : err.message),
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
      this.actionLoading = null;
    },

    openSupplierReplacedDialog(item) {
      this.selectedItem = item;
      // Default to the original RMA item, but only if it's still active.
      const original = (this.items || []).find((i) => i.id === item.item_id);
      this.supplierReplacedTargetItemId =
        original && !original.is_deleted ? item.item_id : null;
      this.supplierReplacedDialog = true;
    },
    async submitSupplierReplaced() {
      if (!this.supplierReplacedTargetItemId) {
        this.$alert({
          title: "Validation",
          message: "Please choose which item gets the replacement stock.",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }
      this.actionLoading = this.selectedItem.id;
      try {
        await this.supplier_replaced({
          id: this.selectedItem.id,
          replacement_target_item_id: this.supplierReplacedTargetItemId,
        });
        this.$alert({
          title: "Success",
          message: "Supplier replacement recorded. New stock batch added.",
          color: "success",
          icon: "mdi-check-circle",
        });
        this.supplierReplacedDialog = false;
      } catch (err) {
        this.$alert({
          title: "Error",
          message:
            "Error: " + (err.response ? err.response.data.error : err.message),
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
      this.actionLoading = null;
    },

    openSupplierCreditDialog(item) {
      this.selectedItem = item;
      this.creditAmount = item.stock_lot
        ? item.stock_lot.unit_cost * item.quantity
        : 0;
      this.supplierCreditDialog = true;
    },
    async submitSupplierCredited() {
      if (!this.creditAmount || this.creditAmount <= 0) {
        this.$alert({
          title: "Validation",
          message: "Please enter a valid credit amount.",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }
      this.actionLoading = this.selectedItem.id;
      try {
        await this.supplier_credited({
          id: this.selectedItem.id,
          credit_amount: this.creditAmount,
        });
        this.$alert({
          title: "Success",
          message: "Supplier credit recorded.",
          color: "success",
          icon: "mdi-check-circle",
        });
        this.supplierCreditDialog = false;
      } catch (err) {
        this.$alert({
          title: "Error",
          message:
            "Error: " + (err.response ? err.response.data.error : err.message),
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
      this.actionLoading = null;
    },

    async confirmWriteOff(item) {
      let x = await this.$confirm({
        title: "Write Off (No Supplier Claim)",
        message:
          "Write off this defective unit with no supplier claim (e.g. out of supplier warranty)? If the customer was already replaced, its cost is booked as a loss.",
        color: "red",
        icon: "mdi-close-circle",
      });
      if (!x) return;
      this.actionLoading = item.id;
      try {
        await this.supplier_write_off(item.id);
        this.$alert({
          title: "Success",
          message: "Defective unit written off.",
          color: "success",
          icon: "mdi-check-circle",
        });
      } catch (err) {
        this.$alert({
          title: "Error",
          message:
            "Error: " + (err.response ? err.response.data.error : err.message),
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
      this.actionLoading = null;
    },

    // === Print / delete ===
    print_all() {
      let url = this.default_server_url + "/print_all_returned_items";
      window.open(url);
    },
    print_selected_multiple() {
      let url =
        this.default_server_url +
        "/print_multiple_returned_items/" +
        JSON.stringify(this.checkbox_array);
      window.open(url);
    },
    print_multi() {
      this.print_multiple = !this.print_multiple;
      this.checkbox_array = [];
    },
    add_checkbox(item) {
      const itemId = item.id;
      if (!this.checkbox_array.includes(itemId)) {
        this.checkbox_array.unshift(itemId);
      } else {
        this.checkbox_array = this.checkbox_array.filter((i) => i !== itemId);
      }
    },
    print(item) {
      this.printReturnedItem(item);
    },
    async del(item) {
      let x = await this.$confirm({
        title: "Delete Item",
        message: "Are you sure you want to delete this item?",
        color: "red",
        icon: "mdi-delete",
      });
      if (x) {
        this.loading = true;
        this.delete_returned_item(item.id)
          .then(() => {
            this.$alert({
              title: "Success",
              message: "Item successfully deleted",
              color: "success",
              icon: "mdi-check-circle",
            });
            this.loading = false;
          })
          .catch((err) => {
            this.$alert({
              title: "Error",
              message:
                "Error: " +
                (err.response ? err.response.data.error : err.message),
              color: "error",
              icon: "mdi-alert-circle",
            });
            this.loading = false;
          });
      }
    },
    ...mapActions({
      get_returned_items: "item/get_returned_items",
      get_items: "item/get_items",
      delete_returned_item: "item/delete_returned_item",
      edit_returned_item: "item/edit_returned_item",
      give_replacement: "item/give_replacement",
      mark_supplier_return: "item/mark_supplier_return",
      return_to_supplier: "item/return_to_supplier",
      supplier_replaced: "item/supplier_replaced",
      supplier_credited: "item/supplier_credited",
      supplier_write_off: "item/supplier_write_off",
    }),
  },
  created() {
    if (this.returned_items.length <= 0) this.get_returned_items();
    if (!this.items || this.items.length <= 0) this.get_items();
  },
};
</script>

<style lang="scss" scoped></style>
